import type { UseQueryOptions } from '@tanstack/react-query'

import { match } from 'ts-pattern'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  InfiniteQueryOptions,
  WilayahDataType,
  WilayahResponsDataType
} from './types'

export type Region = 'province' | 'city' | 'district' | 'village'

const WILAYAH_QUERY_KEY = (params: {
  page: number
  per_page: number
  search: string
  type: Region
  id_provinsi?: string
  id_kota?: string
  id_kecamatan?: string
}) => ['wilayah', { ...params }] as const

type WilayahQueryKey = ReturnType<typeof WILAYAH_QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
    type: Region
    id_provinsi: string
    id_kota: string
    id_kecamatan: string
  }>
): InfiniteQueryOptions<WilayahQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 50,
    search: opts?.search ?? '',
    type: opts?.type ?? 'province',
    id_provinsi: opts?.id_provinsi,
    id_kota: opts?.id_kota,
    id_kecamatan: opts?.id_kecamatan
  }

  return {
    queryKey: WILAYAH_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }) => {
      try {
        const region = match(params.type)
          .with('province', () => 'provinsi')
          .with('city', () => 'kota')
          .with('district', () => 'kecamatan')
          .with('village', () => 'desa')
          .exhaustive()

        const res = await api.get<WilayahResponsDataType>(
          `/referensi/wilayah-indonesia/${region}`,
          {
            params: {
              page: pageParam,
              per_page: params.per_page,
              nama:
                params.search.length > 3
                  ? `%${params.search.toUpperCase()}%`
                  : undefined,
              sort: 'nama,asc',
              mode: 'and',
              id_provinsi: params.id_provinsi,
              id_kota: params.id_kota,
              id_kecamatan: params.id_kecamatan
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const WILAYAH_BY_CODE_QUERY_KEY = (params: { code: string; type: Region }) =>
  ['wilayah', params] as const

const byCode = (opts: {
  code: string
  type: Region
}): UseQueryOptions<ServiceGetByIdResponse<WilayahDataType>> => ({
  queryKey: WILAYAH_BY_CODE_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const region = match(opts.type)
        .with('province', () => 'provinsi')
        .with('city', () => 'kota')
        .with('district', () => 'kecamatan')
        .with('village', () => 'desa')
        .exhaustive()
      const res = await api.get<ServiceGetAllResponse<WilayahDataType[]>>(
        `/referensi/wilayah-indonesia/${region}`,
        {
          params: {
            kode: opts.code,
            sort: 'nama,asc',
            page: 1,
            per_page: 10,
            mode: 'or'
          }
        }
      )

      const data = res.data.data?.[0]
      if (!data) {
        throw new Error(`Kode wilayah ${opts.code} tidak dapat ditemukan.`)
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const wilayahReference = {
  queryKey: {
    all: WILAYAH_QUERY_KEY,
    byCode: WILAYAH_BY_CODE_QUERY_KEY
  },
  batched: getBatch,
  byCode
}
